/**
 * Web component for newsletter subscription form: intl-tel-input, phone label focus,
 * dial code handling, and checker-group validation.
 */
class SubscriptionForm extends HTMLElement {
  connectedCallback() {
    this.setupPopupDelegate();
    this.setupPhoneInput();
    this.setupPhoneLabelFocus();
    this.setupDialCodeOnChange();
    this.setupFormValidation();
    this.setupUrlParams();
  }

  get initialCountry() {
    return this.dataset.initialCountry || 'auto';
  }

  get countryOrder() {
    const raw = this.dataset.countryOrder;
    if (!raw || typeof raw !== 'string') return ['ae', 'sa', 'bh', 'om', 'kw'];
    return raw.split(',').map((c) => c.trim()).filter(Boolean);
  }

  get urlParams() {
    const raw = this.dataset.urlParams;
    if (!raw || typeof raw !== 'string') return [];
    return raw.split(',').map((p) => p.trim()).filter(Boolean);
  }

  setupUrlParams() {
    const form = this.querySelector('form');
    if (!form || this.urlParams.length === 0) return;

    try {
      const params = this.urlParams;
      const fragment = document.createDocumentFragment();
      const storedParams = new URLSearchParams();

      params.forEach((name) => {
        const storedValue = sessionStorage.getItem(`subscribe_${name}`);
        if (storedValue) {
          const input = this.createInput(name, storedValue);
          fragment.appendChild(input);
          storedParams.set(name, storedValue);
        }
      });

      const needUrlParams = params.some((name) => !sessionStorage.getItem(`subscribe_${name}`));
      if (needUrlParams) {
        const urlParams = new URLSearchParams(window.location.search);
        params.forEach((name) => {
          if (!sessionStorage.getItem(`subscribe_${name}`)) {
            const urlValue = urlParams.get(name);
            if (urlValue) {
              sessionStorage.setItem(`subscribe_${name}`, urlValue);
              const input = this.createInput(name, urlValue);
              fragment.appendChild(input);
              storedParams.set(name, urlValue);
            }
          }
        });
      }

      if (fragment.children.length) {
        form.appendChild(fragment);
      }

      const continueButton = form.querySelector('.success-continue');
      if (continueButton && storedParams.size > 0) {
        continueButton.href = `${continueButton.href.split('?')[0]}?${storedParams.toString()}`;
      }
    } catch (err) {
      console.warn('SubscriptionForm: error initializing url params', err);
    }
  }

  setupPopupDelegate() {
    const popupBtn = document.querySelector('.subscribe_button');
    const toSubscribe = document.querySelector('.to-subscribe');
    if (toSubscribe && popupBtn) {
      toSubscribe.addEventListener('click', (e) => {
        e.preventDefault();
        popupBtn.click();
      });
    }
  }

  setupPhoneInput() {
    this.phoneInput = this.querySelector('input[type="tel"]');
    if (!this.phoneInput || typeof intlTelInput !== 'function') return;

    intlTelInput(this.phoneInput, {
      initialCountry: this.initialCountry,
      separateDialCode: true,
      countryOrder: this.countryOrder,
      fixDropdownWidth: false,
    });
  }

  setupPhoneLabelFocus() {
    const phoneLabel = this.phoneInput.labels[0];

    if (!phoneLabel) return;

    this.phoneInput.addEventListener('focus', () => phoneLabel.classList.add('focus'));
    this.phoneInput.addEventListener('focusout', () => {
      if (this.phoneInput.value === '') phoneLabel.classList.remove('focus');
    });
  }

  setupDialCodeOnChange() {
    this.phoneInput.addEventListener('change', (e) => {
      const codeEl = this.querySelector('.iti__selected-dial-code');
      const code = codeEl?.textContent ?? '';
      const num = e.target.value;
      if (num !== '' && code && !num.includes(code)) {
        e.target.value = code + num;
      }
    });
  }

  setupFormValidation() {
    const form = this.querySelector('form');
    const submitButton = this.querySelector('button[type="submit"]');
    const checkerGroups = this.querySelectorAll('.checker-group');

    if (!form || !submitButton) return;

    const validateCheckerGroup = (group, showErrors = true) => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]');
      const errorElement = group.querySelector('.form-group__error');
      const hasChecked = Array.from(checkboxes).some((cb) => cb.checked);

      if (!hasChecked) {
        if (showErrors && errorElement) errorElement.classList.remove('hidden');
        return false;
      }
      if (errorElement) errorElement.classList.add('hidden');
      return true;
    };

    const validateForm = (showErrors = false) => {
      let isValid = true;
      checkerGroups.forEach((group) => {
        if (!validateCheckerGroup(group, showErrors)) isValid = false;
      });
      submitButton.disabled = !isValid;
      return isValid;
    };

    checkerGroups.forEach((group) => {
      group.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => validateForm(true));
      });
    });

    form.addEventListener('submit', (e) => {
      if (!validateForm(true)) {
        e.preventDefault();
        return false;
      }
    });

    validateForm(false);
  }

  createInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `contact[note][${name}]`;
    input.value = value;
    return input;
  }
}

if (!customElements.get('subscription-form')) {
  customElements.define('subscription-form', SubscriptionForm);
}
